import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  InputAdornment
} from "@mui/material";
import {
  Edit as EditIcon,
  ManageAccounts as ManageIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Groups as GroupsIcon,
  PeopleAlt as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { BASEURL } from "../../Constants";

const BRAND = "#005CC5";

// ---- Helpers lifted from your Teams page for visual consistency ----
const stringToColor = (str) => {
  const s = typeof str === "string" ? str : String(str || "");
  if (!s.length) return "#9e9e9e"; // safe fallback color
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const initials = (emailOrName) => {
  const s = typeof emailOrName === "string" ? emailOrName : String(emailOrName || "");
  if (!s) return "??";
  const name = s.split("@")[0];
  const parts = name.replace(/[^a-zA-Z0-9 ]/g, " ").trim().split(/\s+/);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return (parts[0] || "").slice(0, 2).toUpperCase() || "??";
  return ((parts[0][0] || "?") + (parts[1][0] || "?")).toUpperCase();
};

const membershipColor = (membership) => {
  switch ((membership || "").toLowerCase()) {
    case "beta":
      return "info";
    case "pro":
      return "success";
    case "free":
      return "default";
    default:
      return "default";
  }
};

// Known keys (still handles arbitrary extras)
const KNOWN_LIMIT_KEYS = [
  "external_assets",
  "web_scans",
  "api_scans",
  "vishing_calls",
  "darkweb_scans",
];

const ensureLimitDefaults = (usage_limit) => {
  const out = { ...(usage_limit || {}) };
  KNOWN_LIMIT_KEYS.forEach((k) => {
    if (out[k] == null || Number.isNaN(Number(out[k]))) out[k] = 0;
  });
  // Ensure any other keys are numeric
  Object.keys(out).forEach((k) => {
    if (out[k] == null || Number.isNaN(Number(out[k]))) out[k] = 0;
  });
  return out;
};

// ---------------- Component ----------------
const AdminPanel = () => {
  const token = sessionStorage.getItem("token");

  // Data
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);

  // UI state
  const [tab, setTab] = useState(0); // 0 = Teams, 1 = Users
  const [toast, setToast] = useState({ open: false, severity: "success", msg: "" });

  // Editors
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [limitsTeam, setLimitsTeam] = useState(null);
  const [limitsDraft, setLimitsDraft] = useState({});

  const [viewUsersOpen, setViewUsersOpen] = useState(false);
  const [viewUsersTeam, setViewUsersTeam] = useState(null);

  const [membershipDrafts, setMembershipDrafts] = useState({}); // teamId -> value

  // Filters
  const [teamSearch, setTeamSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // ---------- API ----------
  const fetchAllTeams = async () => {
    const res = await fetch(`${BASEURL}/admin/teams`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success === 1) return data.teams || [];
    throw new Error(data.message || "Failed to load teams");
  };

  const fetchAllUsers = async () => {
    const res = await fetch(`${BASEURL}/admin/users`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success === 1) return data.users || [];
    throw new Error(data.message || "Failed to load users");
  };

  const updateMembership = async (teamId, membership) => {
    const res = await fetch(`${BASEURL}/admin/team/membership`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ teamId, membership }),
    });
    return res.json();
  };

  const updateUsageLimit = async (teamId, usage_limit) => {
    const res = await fetch(`${BASEURL}/admin/team/usage`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ teamId, usage_limit }),
    });
    return res.json();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([fetchAllTeams(), fetchAllUsers()]);
      // normalize limits defaults
      const normalized = t.map((team) => ({
        ...team,
        id: team.id ?? team.teamId, // ensure we have an id
        usage_limit: ensureLimitDefaults(team.usage_limit),
      }));
      setTeams(normalized);
      setUsers(u);
      // prime membership drafts
      const drafts = {};
      normalized.forEach((t) => (drafts[t.id] = t.membership || "free"));
      setMembershipDrafts(drafts);
    } catch (e) {
      setToast({ open: true, severity: "error", msg: "Failed to load admin data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------- Handlers ----------
  const openLimits = (team) => {
    if (!team.id && !team.teamId) {
      setToast({ open: true, severity: "error", msg: "Team is missing an id; cannot edit limits." });
      return;
    }
    setLimitsTeam(team);
    setLimitsDraft(ensureLimitDefaults(team.usage_limit));
    setLimitsOpen(true);
  };

  const saveLimits = async () => {
    try {
      const payload = {};
      Object.keys(limitsDraft).forEach((k) => (payload[k] = Number(limitsDraft[k] || 0)));
      const res = await updateUsageLimit(limitsTeam.id ?? limitsTeam.teamId, payload);
      if (res.success === 1) {
        setToast({ open: true, severity: "success", msg: `Updated limits for ${limitsTeam.teamName}.` });
        setLimitsOpen(false);
        setLimitsTeam(null);
        await load();
      } else {
        throw new Error(res.message || "error");
      }
    } catch (e) {
      setToast({ open: true, severity: "error", msg: "Could not update limits." });
    }
  };

  const saveMembership = async (teamId) => {
    try {
      const res = await updateMembership(teamId, membershipDrafts[teamId] || "free");
      if (res.success === 1) {
        setToast({ open: true, severity: "success", msg: `Membership saved.` });
        await load();
      } else {
        throw new Error(res.message || "error");
      }
    } catch (e) {
      setToast({ open: true, severity: "error", msg: "Could not update membership." });
    }
  };

  const addLimitKey = () => {
    const key = prompt("New limit key (snake_case):");
    if (!key) return;
    setLimitsDraft((d) => ({ ...d, [key]: Number(d[key] || 0) }));
  };

  // ---------- Derived ----------
  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    const q = teamSearch.toLowerCase();
    return teams.filter((t) =>
      (t.teamName || "").toLowerCase().includes(q) ||
      (t.membership || "").toLowerCase().includes(q) ||
      (t.users || []).some((u) => (u.email || "").toLowerCase().includes(q))
    );
  }, [teams, teamSearch]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users;
    const q = userSearch.toLowerCase();
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.teams || []).some((t) => (t.teamName || "").toLowerCase().includes(q))
    );
  }, [users, userSearch]);

  // ---------- UI ----------
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${BRAND} 0%, #003a88 100%)`,
          color: "white",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
              Admin
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Manage teams, memberships, and usage limits; browse all users.
            </Typography>
          </Box>
          <ManageIcon sx={{ fontSize: 44, opacity: 0.9 }} />
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ mt: 2,
            "& .MuiTab-root": { color: alpha("#fff", 0.9), fontWeight: 700, textTransform: "none" },
            "& .Mui-selected": { color: "#fff" }
          }}
        >
          <Tab label={<Stack direction="row" spacing={1} alignItems="center"><GroupsIcon/> <span>Teams</span></Stack>} />
          <Tab label={<Stack direction="row" spacing={1} alignItems="center"><PeopleIcon/> <span>Users</span></Stack>} />
        </Tabs>
      </Card>

      {/* Teams Tab */}
      {tab === 0 && (
        <Card component={Paper} elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Teams Overview
            </Typography>
            <TextField
              size="small"
              placeholder="Search teams or users…"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {filteredTeams.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Typography>No teams to display.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Team</TableCell>
                    <TableCell>Membership</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>Usage</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTeams.map((t) => {
                    const usersPreview = (t.users || []).slice(0, 6);
                    const extra = Math.max(0, (t.users || []).length - usersPreview.length);
                    const usageCount = Number(t.usage_count) || 0;
                    const limitTotal = Object.values(t.usage_limit || {}).reduce((a, b) => a + Number(b || 0), 0);
                    return (
                      <TableRow key={t.id || t.teamName} hover>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontWeight: 700 }}>{t.teamName}</Typography>
                            {t.membership && (
                              <Chip size="small" label={t.membership} color={membershipColor(t.membership)} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel id={`mem-${t.id || t.teamName}`}>Membership</InputLabel>
                              <Select
                                labelId={`mem-${t.id || t.teamName}`}
                                label="Membership"
                                value={membershipDrafts[t.id] ?? (t.membership || "free")}
                                onChange={(e) =>
                                  setMembershipDrafts((d) => ({ ...d, [t.id]: e.target.value }))
                                }
                              >
                                <MenuItem value="free">Free</MenuItem>
                                <MenuItem value="beta">Beta</MenuItem>
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SaveIcon />}
                              onClick={() => saveMembership(t.id || t.teamId)}
                            >
                              Save
                            </Button>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {usersPreview.map((u) => {
                              const email = (u && u.email) ? u.email : "(no email)";
                              return (
                                <Chip
                                  key={u.id}
                                  size="small"
                                  avatar={<Avatar sx={{ bgcolor: stringToColor(email) }}>{initials(email)}</Avatar>}
                                  label={email}
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              );
                            })}
                            {extra > 0 && (
                              <Chip
                                size="small"
                                label={`+${extra} more`}
                                onClick={() => {
                                  setViewUsersTeam(t);
                                  setViewUsersOpen(true);
                                }}
                                icon={<ExpandMoreIcon />}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2"><b>Used:</b> {usageCount}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              <b>Limit sum:</b> {limitTotal}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openLimits(t)}
                          >
                            Edit Limits
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* Users Tab */}
      {tab === 1 && (
        <Card component={Paper} elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Users
            </Typography>
            <TextField
              size="small"
              placeholder="Search users or teams…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {filteredUsers.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Typography>No users found.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredUsers.map((u) => (
                <Grid item xs={12} md={6} key={u.email}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Avatar sx={{ bgcolor: stringToColor(u.email) }}>{initials(u.email)}</Avatar>
                      <Typography sx={{ fontWeight: 700 }}>{u.email}</Typography>
                      <Tooltip title="Copy email">
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(u.email)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {(u.teams || []).map((t) => (
                        <Chip key={`${u.email}-${t.teamName}`} size="small" label={`${t.teamName} • ${t.role || "member"}`} />
                      ))}
                      {(u.teams || []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">No teams</Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Card>
      )}

      {/* --- Dialog: Team Users (View More) --- */}
      <Dialog open={viewUsersOpen} onClose={() => setViewUsersOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Users — {viewUsersTeam?.teamName}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(viewUsersTeam?.users || []).map((u) => (
              <Chip
                key={u.id}
                avatar={<Avatar sx={{ bgcolor: stringToColor(u.email) }}>{initials(u.email)}</Avatar>}
                label={u.email}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
            {(viewUsersTeam?.users || []).length === 0 && (
              <Typography color="text.secondary">No users on this team.</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUsersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* --- Dialog: Edit Limits --- */}
      <Dialog open={limitsOpen} onClose={() => setLimitsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Limits — {limitsTeam?.teamName}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set per-key numeric limits. Missing or null values default to 0.
          </Typography>

          {/* Known keys first for consistency */}
          {KNOWN_LIMIT_KEYS.map((k) => (
            <TextField
              key={k}
              type="number"
              label={k}
              value={limitsDraft[k] ?? 0}
              onChange={(e) => setLimitsDraft((d) => ({ ...d, [k]: e.target.value }))}
              sx={{ mr: 2, mb: 2, width: { xs: "100%", sm: 220 } }}
              InputProps={{ inputProps: { min: 0 } }}
            />
          ))}

          {/* Any extra keys present on the team */}
          {Object.keys(limitsDraft)
            .filter((k) => !KNOWN_LIMIT_KEYS.includes(k))
            .map((k) => (
              <TextField
                key={k}
                type="number"
                label={k}
                value={limitsDraft[k] ?? 0}
                onChange={(e) => setLimitsDraft((d) => ({ ...d, [k]: e.target.value }))}
                sx={{ mr: 2, mb: 2, width: { xs: "100%", sm: 220 } }}
                InputProps={{ inputProps: { min: 0 } }}
              />
            ))}

          <Box sx={{ mt: 1 }}>
            <Button variant="text" onClick={addLimitKey}>
              + Add custom key
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitsOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveLimits}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toasts */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
